import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  LinearProgress,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import HomeIcon from '@mui/icons-material/Home'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { TestAnswerFormatSection } from '@/components/tests/sections/TestAnswerFormatSection'
import { TestCheckGoalSection } from '@/components/tests/sections/TestCheckGoalSection'
import { TestCompletionModeSection } from '@/components/tests/sections/TestCompletionModeSection'
import { TestDisplaySection } from '@/components/tests/sections/TestDisplaySection'
import { TestHintsSection } from '@/components/tests/sections/TestHintsSection'
import { TestTemplateSection } from '@/components/tests/sections/TestTemplateSection'
import { TestWordFiltersSection } from '@/components/tests/sections/TestWordFiltersSection'
import { STUDENT_SELF_PROFILE } from '@/components/tests/testCreateProfiles'
import { readTemplateSettings, templateConditionsToRows } from '@/components/tests/templateSettings'
import { completionModeIsComplete } from '@/components/tests/testSettingsTypes'
import { useTestSettingsForm } from '@/components/tests/useTestSettingsForm'
import type { TestTemplateId } from '@/components/tests/sections/TestTemplateSection'
import { testsApi, type TestPreviewRunRequestPayload } from '@/api/tests'
import { tagsApi, type Tag } from '@/api/tagsApi'
import { getEnums, type EnumOption } from '@/api/enums'
import { getWordAttributeFilterKeys } from '@/constants/wordAttributeFilterKeys'
import { ROUTES } from '@/utils/constants'
import { getErrorMessage } from '@/utils/error'
import { useAuthStore } from '@/stores/authStore'
import { answerTypeFormatMode, type AnswerFormatMode } from '@/utils/testPromptAnswerMatrix'
import type { TestTemplate } from '@/types'
import '@/styles/dashboard.css'

/** Конструктор самостоятельного теста: возможности формы собраны в один профиль. */
const PROFILE = STUDENT_SELF_PROFILE

const PREVIEW_DEBOUNCE_MS = 3_000

type PreviewState = {
  count_need: number
  count_found: number
  shortage: number
  card_ids: number[]
}

function SelfPreviewPanel(props: {
  preview: PreviewState | null
  isLoading: boolean
  selectionBlockHint: string | null
  canRunPreview: boolean
}) {
  const { preview, isLoading, selectionBlockHint, canRunPreview } = props
  const { t } = useTranslation()
  const pct = preview?.count_need ? Math.min(100, Math.round((preview.count_found / preview.count_need) * 100)) : 0
  const hasShortage = Boolean(preview && preview.shortage > 0)

  return (
    <Paper variant="outlined" sx={{ p: 2, position: { md: 'sticky' }, top: { md: 16 } }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
        {t('tests.preview.panelTitle')}
      </Typography>

      {isLoading ? <LinearProgress sx={{ mb: 2 }} /> : <Box sx={{ height: 4, mb: 2 }} />}

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Paper variant="outlined" sx={{ p: 1.25, flex: 1 }}>
          <Typography
            variant="h5"
            sx={(theme) => ({ fontFamily: 'monospace', lineHeight: 1.1, color: theme.palette.primary.main })}
          >
            {preview?.count_need ?? '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('tests.preview.wordsPlanned')}
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 1.25, flex: 1 }}>
          <Typography variant="h5" sx={{ fontFamily: 'monospace', lineHeight: 1.1 }}>
            {preview ? Math.max(0, preview.shortage) : '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('tests.preview.shortage')}
          </Typography>
        </Paper>
      </Stack>

      {preview ? (
        <Box sx={{ mb: 2 }}>
          <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 99, mb: 0.75 }} />
          <Typography variant="caption" color="text.secondary">
            {preview.count_found}/{preview.count_need}
          </Typography>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
          {selectionBlockHint ??
            (canRunPreview
              ? t('tests.preview.autoHint')
              : t('tests.createForm.previewNeedDisplayAndFormatHint', {
                  display: t('tests.createForm.sectionDisplay'),
                  answerFormat: t('tests.createForm.sectionAnswerFormat'),
                }))}
        </Typography>
      )}

      {hasShortage && preview ? (
        <Alert severity="error" sx={{ mb: 2, py: 0.5 }}>
          {t('tests.preview.summary', {
            found: preview.count_found,
            need: preview.count_need,
            shortage: preview.shortage,
          })}
        </Alert>
      ) : null}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {t('tests.preview.cardIds')}
      </Typography>
      <Paper variant="outlined" sx={{ maxHeight: 360, overflow: 'auto' }}>
        <List dense disablePadding>
          {(preview?.card_ids ?? []).length === 0 ? (
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                primary="—"
              />
            </ListItem>
          ) : (
            (preview?.card_ids ?? []).map((cid) => (
              <ListItem key={cid} divider>
                <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={`#${cid}`} />
              </ListItem>
            ))
          )}
        </List>
      </Paper>
    </Paper>
  )
}

/**
 * Шаблон целиком укладывается в возможности формы.
 *
 * Не укладывающийся в списке не показываем: применить его целиком нельзя, а
 * применить наполовину — значит молча собрать не тот тест, который обещала
 * плитка. Поэтому проверяем не только формат ответа, но и всё, чего у формы
 * ученика нет: выключенную автопроверку и конкретные карточки.
 */
function templateFitsProfile(tpl: TestTemplate, allowedModes: AnswerFormatMode[]): boolean {
  const settings = (tpl.settings ?? {}) as Record<string, unknown>
  const answerTypes = Array.isArray(settings.enabled_answer_type)
    ? settings.enabled_answer_type
    : typeof settings.answer_type === 'string'
      ? [settings.answer_type]
      : []

  if (settings.auto_check_enabled === false) return false
  if (Array.isArray(settings.explicit_card_ids) && settings.explicit_card_ids.length > 0) return false

  return answerTypes.every((at) => {
    const mode = answerTypeFormatMode(String(at))
    return mode === null || allowedModes.includes(mode)
  })
}

export function SelfTestCreatePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const me = useAuthStore((s) => s.user)
  const canUseCustomTemplates = useAuthStore(
    (s) => s.user?.subscription_parameters?.custom_test_templates_enabled !== 'false',
  )

  const form = useTestSettingsForm(PROFILE)

  const [templates, setTemplates] = useState<TestTemplate[] | null>(null)
  const [templatesLoading, setTemplatesLoading] = useState(true)
  const [selectedTemplate, setSelectedTemplate] = useState<TestTemplateId>('none')

  const [tags, setTags] = useState<Tag[]>([])
  const [enumOptions, setEnumOptions] = useState<Record<string, EnumOption[]>>({})
  const wordAttributeKeys = useMemo(() => getWordAttributeFilterKeys(enumOptions), [enumOptions])

  const [isPreviewLoading, setIsPreviewLoading] = useState(false)
  const [preview, setPreview] = useState<PreviewState | null>(null)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const previewTimerRef = useRef<number | null>(null)
  const hasInitialPreviewRef = useRef(false)
  /** Номер запроса превью: ответ обгоняющего его предшественника игнорируем. */
  const previewSeqRef = useRef(0)

  useEffect(() => {
    let cancelled = false
    tagsApi
      .getList({ per_page: 100 })
      .then((r) => {
        if (!cancelled) setTags(r.data)
      })
      .catch(() => {})
    getEnums()
      .then((x) => {
        if (!cancelled) setEnumOptions(x)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    setTemplatesLoading(true)
    testsApi
      .listTemplates()
      .then((list) => {
        if (cancelled) return
        const allowed = list
          .filter((x) => canUseCustomTemplates || x.is_system)
          .filter((x) => templateFitsProfile(x, PROFILE.answerFormatModes))
        setTemplates(allowed)
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [canUseCustomTemplates])

  const {
    setEnabledPromptTypes,
    setEnabledAnswerFormatModes,
    setTagIds,
    setAttributeConditions,
    setRelaxFilters,
    setMode,
    setCountNeed,
    setDurationMin,
    setSeriesTarget,
    setAllowHints,
    setAllowTranscription,
  } = form

  /**
   * Применение шаблона: общий разбор настроек, затем те поля, что есть у формы.
   *
   * Автопроверку и конкретные карточки ученику не отдаём — их в этой форме нет;
   * шаблоны, которые на них завязаны, до списка не доходят
   * ({@link templateFitsProfile}).
   */
  const handleTemplateChange = useCallback(
    (id: TestTemplateId) => {
      if (id === 'none') {
        setSelectedTemplate('none')
        return
      }

      const tpl = (templates ?? []).find((x) => x.id === id)
      if (!tpl) return

      setSelectedTemplate(id)

      const patch = readTemplateSettings(tpl, {
        promptTypes: PROFILE.promptTypes,
        answerFormatModes: PROFILE.answerFormatModes,
        attributeKeys: wordAttributeKeys,
      })

      if (patch.promptTypes) setEnabledPromptTypes(patch.promptTypes)
      if (patch.answerFormatModes) setEnabledAnswerFormatModes(patch.answerFormatModes)
      if (patch.mode) setMode(patch.mode)
      if (patch.countNeed !== undefined) setCountNeed(patch.countNeed)
      if (patch.durationMin !== undefined) setDurationMin(patch.durationMin)
      if (patch.seriesTarget !== undefined) setSeriesTarget(patch.seriesTarget)
      if (patch.allowHints !== undefined) setAllowHints(patch.allowHints)
      if (patch.allowTranscription !== undefined) setAllowTranscription(patch.allowTranscription)
      if (patch.tagIds) setTagIds(patch.tagIds)
      if (patch.relaxFilters !== undefined) setRelaxFilters(patch.relaxFilters)
      if (patch.attributeConditions) {
        const rows = templateConditionsToRows(patch.attributeConditions)
        setAttributeConditions(() => rows)
      }
    },
    [
      templates,
      wordAttributeKeys,
      setEnabledPromptTypes,
      setEnabledAnswerFormatModes,
      setMode,
      setCountNeed,
      setDurationMin,
      setSeriesTarget,
      setAllowHints,
      setAllowTranscription,
      setTagIds,
      setRelaxFilters,
      setAttributeConditions,
    ],
  )

  /**
   * Любая ручная правка снимает выбор шаблона.
   *
   * Оборачиваем сеттеры, а не следим за настройками: подсвеченная плитка,
   * которая уже не описывает форму, врёт ученику прямо перед кнопкой «Создать».
   * Внутренние синхронизации хука идут мимо обёрток и выбор не сбивают.
   */
  const manual = useCallback(
    <A,>(setter: (value: A) => void) =>
      (value: A) => {
        setSelectedTemplate('none')
        setter(value)
      },
    [],
  )

  const previewRequestPayload = useMemo((): TestPreviewRunRequestPayload | null => {
    const uid = me?.id
    if (uid == null || !Number.isFinite(Number(uid))) return null

    const attributeConditions = form.attributeConditions
      .filter((c) => c.operator === 'has' || c.operator === 'has_not' || c.value.trim() !== '')
      .map((c) => ({ field: c.field, operator: c.operator, value: c.value.trim() }))

    return {
      target_id: Number(uid),
      target_type: 'user',
      exclude_duplicate_words: false,
      enabled_prompt_type: form.enabledPromptTypes as string[],
      enabled_answer_type: form.granularEnabledAnswerTypes,
      auto_check_enabled: form.autoCheckEnabled,
      filters: {
        relax_filter: form.relaxFilters,
        tag_ids: form.tagIds,
        attribute_conditions: attributeConditions,
      },
      completion_method: {
        mode: form.mode,
        ...(form.mode === 'timed' && form.durationMin !== '' ? { duration_min: String(form.durationMin) } : {}),
        ...(form.mode === 'series' && form.seriesTarget !== '' ? { series_target: String(form.seriesTarget) } : {}),
      },
      help_settings: {
        allow_hints: form.allowHints,
        allow_transcription: form.allowTranscription,
      },
      explicit_card_ids: [],
      ...(form.countNeed === '' ? {} : { count_cards: Number(form.countNeed) }),
    }
  }, [
    me?.id,
    form.attributeConditions,
    form.enabledPromptTypes,
    form.granularEnabledAnswerTypes,
    form.autoCheckEnabled,
    form.relaxFilters,
    form.tagIds,
    form.mode,
    form.durationMin,
    form.seriesTarget,
    form.allowHints,
    form.allowTranscription,
    form.countNeed,
  ])

  const canRunPreview = useMemo(() => {
    if (!previewRequestPayload) return false
    if (form.enabledPromptTypes.length === 0) return false
    if (form.compatibleAnswerTypes.length === 0) return false
    if (form.enabledAnswerFormatModes.length === 0) return false
    // Без числа у «На время» и «Серии» сервер отбивает и превью, и создание.
    if (!completionModeIsComplete(form.mode, form.durationMin, form.seriesTarget)) return false
    return true
  }, [
    previewRequestPayload,
    form.enabledPromptTypes.length,
    form.compatibleAnswerTypes.length,
    form.enabledAnswerFormatModes.length,
    form.mode,
    form.durationMin,
    form.seriesTarget,
  ])

  const previewSelectionBlockHint = useMemo(() => {
    if (canRunPreview) return null
    return t('tests.createForm.previewNeedDisplayAndFormatHint', {
      display: t('tests.createForm.sectionDisplay'),
      answerFormat: t('tests.createForm.sectionAnswerFormat'),
    })
  }, [canRunPreview, t])

  const handlePreview = useCallback(async () => {
    if (!previewRequestPayload) return
    const seq = ++previewSeqRef.current
    setError(null)
    setIsPreviewLoading(true)
    try {
      const res = await testsApi.preview(previewRequestPayload)
      if (seq !== previewSeqRef.current) return
      const u0 = Array.isArray(res.users) ? res.users[0] : null
      const cards = Array.isArray(u0?.cards) ? u0.cards : []
      setPreview({
        count_need: (u0?.count_need ?? 0) as number,
        count_found: (u0?.count_found ?? 0) as number,
        shortage: (u0?.shortage ?? 0) as number,
        card_ids: cards.map((c) => Number(c.id)).filter((x) => Number.isFinite(x)),
      })
    } catch (e) {
      if (seq !== previewSeqRef.current) return
      setError(getErrorMessage(e))
    } finally {
      if (seq === previewSeqRef.current) setIsPreviewLoading(false)
    }
  }, [previewRequestPayload])

  useEffect(() => {
    if (!canRunPreview) return
    if (hasInitialPreviewRef.current) return
    hasInitialPreviewRef.current = true
    void handlePreview()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRunPreview])

  useEffect(() => {
    if (!canRunPreview) return
    if (previewTimerRef.current) window.clearTimeout(previewTimerRef.current)
    previewTimerRef.current = window.setTimeout(() => {
      void handlePreview()
    }, PREVIEW_DEBOUNCE_MS)
    return () => {
      if (previewTimerRef.current) window.clearTimeout(previewTimerRef.current)
    }
  }, [canRunPreview, previewRequestPayload, handlePreview])

  useEffect(() => {
    if (canRunPreview) return
    setPreview(null)
    setIsPreviewLoading(false)
  }, [canRunPreview])

  const handleSubmit = async () => {
    if (!previewRequestPayload) return
    setError(null)
    setIsSubmitting(true)
    try {
      const res = await testsApi.createSelf(previewRequestPayload)
      navigate(ROUTES.TEST_RUN(res.id))
    } catch (e) {
      setError(getErrorMessage(e))
    } finally {
      setIsSubmitting(false)
    }
  }

  const displayName = me?.display_name || me?.nickname || me?.email || ''

  return (
    <DashboardLayout>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link
          component={RouterLink}
          to={ROUTES.DASHBOARD}
          underline="hover"
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          {t('dashboard.home')}
        </Link>
        <Link component={RouterLink} to={ROUTES.TESTS} underline="hover" color="inherit">
          {t('tests.title')}
        </Link>
        <Typography color="text.primary">{t('tests.selfCreateTitle')}</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h4" component="h1" fontWeight={700}>
          {t('tests.selfCreateTitle')}
        </Typography>
        <PageHelpPopover pageKey="tests-self-create" />
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 360px' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Stack spacing={2}>
            <TestTemplateSection
              templates={templates}
              loading={templatesLoading}
              selectedTemplate={selectedTemplate}
              onTemplateChange={handleTemplateChange}
            />

            <TestDisplaySection
              options={PROFILE.promptTypes}
              selected={form.enabledPromptTypes}
              onToggle={manual(form.togglePromptType)}
              idPrefix={PROFILE.idPrefix}
              hint={t('tests.form.selfTestPromptLimitedHint')}
            />

            <TestAnswerFormatSection
              modes={PROFILE.answerFormatModes}
              canDisableAutoCheck={PROFILE.canDisableAutoCheck}
              compatibleAnswerTypes={form.compatibleAnswerTypes}
              selectedModes={form.enabledAnswerFormatModes}
              onSelectedModesChange={manual(form.setEnabledAnswerFormatModes)}
              autoCheckEnabled={form.autoCheckEnabled}
              idPrefix={PROFILE.idPrefix}
              hint={t('tests.form.selfTestAutoCheckAlways')}
            />

            <TestCheckGoalSection
              skills={form.derivedActiveSkills}
              hasCompatibleAnswerTypes={form.compatibleAnswerTypes.length > 0}
              hint={t('tests.form.selfTestSkillsRestrictedHint')}
            />

            <TestWordFiltersSection
              tags={tags}
              selectedTagIds={form.tagIds}
              onSelectedTagIdsChange={manual(form.setTagIds)}
              attributeConditions={form.attributeConditions}
              onAttributeConditionsChange={manual(form.setAttributeConditions)}
              attributeKeys={wordAttributeKeys}
              enumOptions={enumOptions}
              relaxFilters={form.relaxFilters}
              onRelaxFiltersChange={manual(form.setRelaxFilters)}
            />

            <TestCompletionModeSection
              mode={form.mode}
              onModeChange={manual(form.setMode)}
              countNeed={form.countNeed}
              onCountNeedChange={manual(form.setCountNeed)}
              countNeedMax={100}
              countNeedHelpKey="tests.createForm.countNeedHelpSelf"
              durationMin={form.durationMin}
              onDurationMinChange={manual(form.setDurationMin)}
              seriesTarget={form.seriesTarget}
              onSeriesTargetChange={manual(form.setSeriesTarget)}
            />

            <TestHintsSection
              allowHints={form.allowHints}
              onAllowHintsChange={manual(form.setAllowHints)}
              transcription={
                PROFILE.supportsTranscription
                  ? { value: form.allowTranscription, onChange: manual(form.setAllowTranscription) }
                  : undefined
              }
              prepMinutes={undefined}
            />
          </Stack>
        </Box>

        <Stack spacing={1.5} sx={{ minWidth: 0 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
              <Avatar
                src={me?.avatar_url ?? undefined}
                sx={(theme) => ({
                  width: 40,
                  height: 40,
                  flexShrink: 0,
                  bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.14 : 0.22),
                  color: 'primary.main',
                  fontWeight: 700,
                })}
              >
                {(displayName || '?').slice(0, 1).toUpperCase()}
              </Avatar>
              <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="body2" color="text.secondary" noWrap>
                  <strong>{t('tests.kind.self', { defaultValue: 'Самостоятельный' })}</strong>
                </Typography>
                {displayName ? (
                  <Typography variant="body2" fontWeight={600} noWrap title={displayName}>
                    {displayName}
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
          </Paper>
          <Button
            onClick={handleSubmit}
            variant="contained"
            size="small"
            fullWidth
            sx={{ textTransform: 'none', height: 40 }}
            disabled={!canRunPreview || isSubmitting}
          >
            {isSubmitting ? (
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                <CircularProgress size={16} color="inherit" />
                <span>{t('common.saving')}</span>
              </Stack>
            ) : (
              t('tests.actions.create')
            )}
          </Button>
          <SelfPreviewPanel
            preview={preview}
            isLoading={isPreviewLoading}
            selectionBlockHint={previewSelectionBlockHint}
            canRunPreview={canRunPreview}
          />
        </Stack>
      </Box>
    </DashboardLayout>
  )
}
